// Clicks and form submissions that land before React has hydrated their control (its button is
// visible in the server HTML, but its handlers aren't attached yet; content inside a Suspense
// boundary hydrates after the rest of the page) are held, then replayed on the same element once
// React has hydrated it, so a control works as soon as it is on screen. React marks the elements it
// has hydrated with a `__reactProps$…` property. Only controls from the server HTML are held, so
// controls other code adds later are never affected; links are left alone, as they work without
// JavaScript. The script runs inline in the server HTML, before any of the app's code.

/**
 * The inline script. It is written as text, not compiled code, so the bundler can't add references
 * to helpers the page hasn't loaded.
 */
export const EARLY_INPUT_SCRIPT = `(() => {
  const hydrated = (element) => Object.keys(element).some((key) => key.startsWith("__reactProps$"));
  // The server HTML's controls: everything parsed so far while the document loads, then those it had.
  const serverControls = new WeakSet();
  document.addEventListener("DOMContentLoaded", () => {
    for (const control of document.querySelectorAll("button, form")) serverControls.add(control);
  });
  const fromServer = (element) => document.readyState === "loading" || serverControls.has(element);
  let held = null;
  const replayWhenHydrated = (element, replay) => {
    held = element;
    const started = Date.now();
    const check = () => {
      if (held !== element || !element.isConnected) return;
      if (hydrated(element)) {
        held = null;
        replay();
      } else if (Date.now() - started < 30000) setTimeout(check, 50);
      else held = null;
    };
    setTimeout(check, 50);
  };
  document.addEventListener("click", (event) => {
    const button = event.target instanceof Element ? event.target.closest("button") : null;
    if (!button || button.disabled || !fromServer(button) || hydrated(button)) return;
    event.preventDefault();
    event.stopPropagation();
    replayWhenHydrated(button, () => {
      if (!button.disabled) button.click();
    });
  }, true);
  document.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !fromServer(form) || hydrated(form)) return;
    event.preventDefault();
    const submitter = event.submitter;
    replayWhenHydrated(form, () => form.requestSubmit(submitter && submitter.isConnected ? submitter : undefined));
  }, true);
})()`;
